import { useEffect, useRef, useCallback } from "react";
import { useNotificationStore, type TNotification } from "./store";
import { fetchAllNotifications, markChapterNotificationAsRead } from "./services";
import { useChapterMemoStore } from "@/zapi-frontend/stores/chapterMemo";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";

const SSE_URL = import.meta.env.VITE_ZAPI_API_URL + "/user/notifications/stream";

function getCurrentChapterId(): number | null {
  const searchParams = new URLSearchParams(window.location.search);
  const chapterId = searchParams.get("chapterId");
  return chapterId ? parseInt(chapterId, 10) : null;
}

export function useNotificationSSE() {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  const { data: isAuthenticated, isLoading: isAuthLoading } = useAuthenticated();

  const setNotifications = useNotificationStore((state) => state.setNotifications);
  const addNotification = useNotificationStore((state) => state.addNotification);
  const markChapterMemoAsLoaded = useChapterMemoStore((state) => state.markChapterAsLoaded);
  const markChatChapterAsLoaded = useChatStore((state) => state.markChapterAsLoaded);

  const connect = useCallback(() => {
    if (eventSourceRef.current?.readyState === EventSource.OPEN) {
      console.log("[SSE] Already connected");
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error("[SSE] Max reconnect attempts reached");
      return;
    }

    try {
      console.log("[SSE] Connecting to notification stream...");
      const eventSource = new EventSource(SSE_URL, {
        withCredentials: true
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log("[SSE] Connected to notification stream");
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event) => {
        try {
          const newNotification: TNotification = JSON.parse(event.data);

          if (newNotification.projectChapterId && newNotification.projectChapterId === getCurrentChapterId()) {
            newNotification.isRead = true;

            markChapterNotificationAsRead(newNotification.projectChapterId).catch((error) => {
              console.error("[SSE] Failed to mark notification as read on backend:", error);
            });
          }

          if (newNotification.type === "chapter_ready" && newNotification.projectChapterId) {
            markChapterMemoAsLoaded(newNotification.projectChapterId);
          }
          if (newNotification.type === "chat_responded" && newNotification.projectChapterId) {
            markChatChapterAsLoaded(newNotification.projectChapterId);
          }

          addNotification(newNotification);
        } catch (error) {
          console.error("[SSE] Failed to parse notification:", error);
        }
      };

      eventSource.onerror = (error) => {
        console.error("[SSE] Connection error:", error);
        eventSource.close();

        const backoffTime = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current++;

        console.log(`[SSE] Reconnecting in ${backoffTime / 1000}s... (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, backoffTime);
      };
    } catch (error) {
      console.error("[SSE] Failed to create EventSource:", error);
    }
  }, [addNotification, markChapterMemoAsLoaded, markChatChapterAsLoaded]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (eventSourceRef.current) {
      console.log("[SSE] Disconnecting from notification stream");
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    reconnectAttemptsRef.current = 0;
  }, []);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      disconnect();
      return;
    }

    fetchAllNotifications()
      .then(setNotifications)
      .then(connect)
      .catch((error) => {
        console.error("Failed to fetch notifications:", error);
      });

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAuthLoading, disconnect]);
}
